import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, MapPin, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { opsApi, type Store, type StoreWrite } from "@/features/operations/api";

const emptyStore: StoreWrite = {
    name: "",
    code: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
    phone: "",
    geo: { lat: 0, lng: 0 },
    service_radius_km: 5,
    is_active: true,
};

type AdminInline = {
    full_name: string;
    email: string;
    phone: string;
    password: string;
};

const emptyAdmin: AdminInline = {
    full_name: "",
    email: "",
    phone: "",
    password: "",
};

export default function AdminStores() {
    const qc = useQueryClient();
    const [dialog, setDialog] = useState<{ open: boolean; editing?: Store }>({ open: false });
    const [form, setForm] = useState<StoreWrite>(emptyStore);
    const [admin, setAdmin] = useState<AdminInline>(emptyAdmin);

    const { data, isLoading } = useQuery({
        queryKey: ["admin", "stores"],
        queryFn: opsApi.listStores,
    });

    const createMut = useMutation({
        mutationFn: (p: StoreWrite & { admin?: AdminInline }) => opsApi.createStore(p),
        onSuccess: (data) => {
            toast.success(
                admin.email
                    ? `Store created · admin invited: ${admin.email}`
                    : "Store created",
            );
            qc.invalidateQueries({ queryKey: ["admin", "stores"] });
            qc.invalidateQueries({ queryKey: ["admin", "team"] });
            setDialog({ open: false });
            setAdmin(emptyAdmin);
            void data;
        },
        onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Failed to create store"),
    });
    const updateMut = useMutation({
        mutationFn: ({ id, p }: { id: string; p: StoreWrite }) => opsApi.updateStore(id, p),
        onSuccess: () => {
            toast.success("Store updated");
            qc.invalidateQueries({ queryKey: ["admin", "stores"] });
            setDialog({ open: false });
        },
    });
    const deleteMut = useMutation({
        mutationFn: (id: string) => opsApi.deleteStore(id),
        onSuccess: () => {
            toast.success("Store deleted");
            qc.invalidateQueries({ queryKey: ["admin", "stores"] });
        },
        onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Failed to delete store"),
    });

    const openNew = () => {
        setForm(emptyStore);
        setAdmin(emptyAdmin);
        setDialog({ open: true });
    };
    const openEdit = (s: Store) => {
        setForm({
            name: s.name,
            code: s.code,
            address_line1: s.address_line1,
            address_line2: s.address_line2,
            city: s.city,
            state: s.state,
            pincode: s.pincode,
            phone: s.phone,
            geo: s.geo,
            service_radius_km: s.service_radius_km,
            is_active: s.is_active,
        });
        setAdmin(emptyAdmin);
        setDialog({ open: true, editing: s });
    };

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (dialog.editing) {
            updateMut.mutate({ id: dialog.editing.id, p: form });
        } else {
            // If any admin field is populated, send the full admin block —
            // backend requires all 4 or none.
            const hasAdmin = Boolean(
                admin.full_name || admin.email || admin.phone || admin.password,
            );
            if (hasAdmin) {
                if (!admin.full_name || !admin.email || !admin.phone || !admin.password) {
                    toast.error(
                        "Fill all 4 store-admin fields (name, email, phone, password) or leave them all blank.",
                    );
                    return;
                }
                if (admin.password.length < 6) {
                    toast.error("Store-admin password must be at least 6 characters");
                    return;
                }
            }
            createMut.mutate(hasAdmin ? { ...form, admin } : form);
        }
    };

    return (
        <div data-testid="admin-stores-page" className="space-y-6">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-extrabold">Stores</h1>
                    <p className="text-sm text-muted-foreground">
                        The physical (dark) store network. Only Super Admin can edit.
                    </p>
                </div>
                <Button onClick={openNew} data-testid="admin-stores-new" className="bg-brand text-white">
                    <Plus className="mr-2 h-4 w-4" /> New store
                </Button>
            </header>

            {isLoading ? (
                <p>Loading…</p>
            ) : !data?.items.length ? (
                <div className="rounded-2xl border border-dashed border-border/60 bg-white p-10 text-center">
                    <Building2 className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                        No stores yet. Create one to unlock multi-store operations.
                    </p>
                </div>
            ) : (
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {data.items.map((s) => (
                        <div
                            key={s.id}
                            data-testid={`admin-store-card-${s.code}`}
                            className="rounded-2xl border border-border/60 bg-white p-5"
                        >
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                        {s.code}
                                    </p>
                                    <p className="text-lg font-bold">{s.name}</p>
                                </div>
                                <span
                                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${s.is_active ? "bg-brand/10 text-brand" : "bg-error/10 text-error"}`}
                                >
                                    {s.is_active ? "Active" : "Paused"}
                                </span>
                            </div>
                            <p className="mt-3 flex items-start gap-1.5 text-sm text-muted-foreground">
                                <MapPin className="mt-0.5 h-3.5 w-3.5" />
                                {s.address_line1}, {s.city} — {s.pincode}
                            </p>
                            <p className="mt-2 text-xs text-muted-foreground">
                                Serves {s.service_radius_km} km · {s.geo.lat.toFixed(3)},{" "}
                                {s.geo.lng.toFixed(3)}
                            </p>
                            <div className="mt-4 flex justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`admin-store-edit-${s.code}`}>
                                    Edit
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-error"
                                    onClick={() => {
                                        if (confirm(`Delete ${s.name}?`)) deleteMut.mutate(s.id);
                                    }}
                                    data-testid={`admin-store-delete-${s.code}`}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={dialog.open} onOpenChange={(v: boolean) => setDialog({ open: v })}>
                <DialogContent data-testid="admin-store-dialog">
                    <DialogHeader>
                        <DialogTitle>{dialog.editing ? "Edit store" : "New store"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={onSubmit} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="s-name">Name</Label>
                                <Input id="s-name" data-testid="admin-store-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                            </div>
                            <div>
                                <Label htmlFor="s-code">Code</Label>
                                <Input id="s-code" data-testid="admin-store-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="s-addr">Address</Label>
                            <Input id="s-addr" data-testid="admin-store-address" value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} required />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label htmlFor="s-city">City</Label>
                                <Input id="s-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required />
                            </div>
                            <div>
                                <Label htmlFor="s-state">State</Label>
                                <Input id="s-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} required />
                            </div>
                            <div>
                                <Label htmlFor="s-pin">Pincode</Label>
                                <Input id="s-pin" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} required />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label htmlFor="s-lat">Latitude</Label>
                                <Input id="s-lat" type="number" step="0.000001" value={form.geo.lat} onChange={(e) => setForm({ ...form, geo: { ...form.geo, lat: Number(e.target.value) } })} required />
                            </div>
                            <div>
                                <Label htmlFor="s-lng">Longitude</Label>
                                <Input id="s-lng" type="number" step="0.000001" value={form.geo.lng} onChange={(e) => setForm({ ...form, geo: { ...form.geo, lng: Number(e.target.value) } })} required />
                            </div>
                            <div>
                                <Label htmlFor="s-radius">Radius (km)</Label>
                                <Input id="s-radius" type="number" step="0.5" min="0.5" value={form.service_radius_km} onChange={(e) => setForm({ ...form, service_radius_km: Number(e.target.value) })} required />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="s-phone">Phone</Label>
                            <Input id="s-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        </div>
                        <div className="flex items-center gap-2">
                            <input id="s-active" type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                            <Label htmlFor="s-active" className="cursor-pointer">Active</Label>
                        </div>

                        {!dialog.editing && (
                            <div className="rounded-xl border border-dashed border-border/70 bg-muted/30 p-3">
                                <p className="mb-2 text-sm font-bold">Store Admin (optional)</p>
                                <p className="mb-3 text-xs text-muted-foreground">
                                    Fill all four fields to auto-create the store-admin account. They'll sign in at <code className="rounded bg-ink/10 px-1">/store/login</code>.
                                </p>
                                <div className="space-y-3">
                                    <div>
                                        <Label htmlFor="sa-name">Admin name</Label>
                                        <Input id="sa-name" data-testid="admin-store-admin-name" value={admin.full_name} onChange={(e) => setAdmin({ ...admin, full_name: e.target.value })} placeholder="Full name" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label htmlFor="sa-email">Admin email</Label>
                                            <Input id="sa-email" type="email" data-testid="admin-store-admin-email" value={admin.email} onChange={(e) => setAdmin({ ...admin, email: e.target.value })} placeholder="manager@yourstore.com" />
                                        </div>
                                        <div>
                                            <Label htmlFor="sa-phone">Admin phone</Label>
                                            <Input id="sa-phone" data-testid="admin-store-admin-phone" value={admin.phone} onChange={(e) => setAdmin({ ...admin, phone: e.target.value })} placeholder="+91…" />
                                        </div>
                                    </div>
                                    <div>
                                        <Label htmlFor="sa-pass">Temporary password</Label>
                                        <Input id="sa-pass" type="password" data-testid="admin-store-admin-password" value={admin.password} onChange={(e) => setAdmin({ ...admin, password: e.target.value })} placeholder="Min. 6 characters" />
                                    </div>
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button type="submit" className="bg-brand text-white" data-testid="admin-store-save" disabled={createMut.isPending || updateMut.isPending}>
                                {dialog.editing ? "Save changes" : "Create store"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
